import { DeclarationDocumentsEnum, declarationDocumentsIdsMapping, DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { hideElementFunction } from '@components';
import { isHighValueDeclaration } from '@utils';

export enum FormDocumentsInputType {
  NUMBER = 'NUMBER',
  INPUT = 'INPUT',
  AUTOCOMPLETE = 'AUTOCOMPLETE',
  DATE = 'DATE',
  MULTILANG = 'MULTILANG',
}

export enum FormDocumentsDeclarationType {
  ALL = 'ALL',
  HIGH_VALUES = 'HIGH_VALUES',
  LOW_VALUES = 'LOW_VALUES',
}

const PLACEHOLDER = {
  DATE_OF_VALIDITY: 'Date of validity',
  REFERENCE_NUMBER: 'Reference Number',
  TYPE: 'Type',
  CODE: 'Code',
  AMOUNT: 'Amount',
  CURRENCY: 'Currency',
  QUANTITY: 'Quantity',
  MEASUREMENT_UNIT: 'Measurement unit and qualifier',
};

export type DeclarationDocumentSchema = Record<
  string,
  {
    id?: string[];
    declarationType?: FormDocumentsDeclarationType;
    fields: Record<
      string,
      {
        declarationType?: FormDocumentsDeclarationType;
        type: FormDocumentsInputType;
        search?: (search: string, lang: string, isHeader?: boolean) => Promise<{ value: string; label: string }[]>;
        code?: string | string[];
        system?: string | string[];
        placeholder: string;
        'data-pw'?: string;
        required?: boolean;
        // trims the edges and rejects any whitespace left inside the value
        noWhitespace?: boolean;
        // trims the edges only, for free-text fields where inner spaces are legitimate
        trimWhitespace?: boolean;
        show?: (context?: { isHeader?: boolean }) => boolean;
        isAmendmentDisabled?: boolean;
        amendmentDisabledCountries?: EnvironmentCountries[];
      }
    >;
  }
>;

export const buildDeclarationDocumentSchema = (country: string, declarationGroup = null, isGoodsItem = false, isExport = false): DeclarationDocumentSchema => {
  const isBEHighValue = country === EnvironmentCountries.BE && isHighValueDeclaration(declarationGroup);
  const isNLExport = country === EnvironmentCountries.NL && isExport;

  return {
    [DeclarationDocumentsEnum.ADDITIONAL_FISCAL_REFERENCE]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.ADDITIONAL_FISCAL_REFERENCE],
      fields: {
        role: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL149',
          placeholder: 'Role',
          noWhitespace: true,
          isAmendmentDisabled: true,
          amendmentDisabledCountries: [EnvironmentCountries.BE],
        },
        vatIdentificationNumber: {
          type: FormDocumentsInputType.INPUT,
          'data-pw': 'vat-identification-number-input',
          placeholder: 'Vat identification number',
          noWhitespace: true,
          required: true,
          isAmendmentDisabled: true,
          amendmentDisabledCountries: [EnvironmentCountries.BE],
        },
        dateOfValidity: {
          type: FormDocumentsInputType.DATE,
          placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
          show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: () => isBEHighValue }),
          isAmendmentDisabled: true,
          amendmentDisabledCountries: [EnvironmentCountries.BE],
        },
      },
    },
    [DeclarationDocumentsEnum.TRANSPORT_DOCUMENTS]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.TRANSPORT_DOCUMENTS],
      fields: {
        type: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL754',
          placeholder: PLACEHOLDER.TYPE,
          noWhitespace: true,
        },
        referenceNumber: {
          type: FormDocumentsInputType.INPUT,
          placeholder: PLACEHOLDER.REFERENCE_NUMBER,
          required: true,
        },
        dateOfValidity: {
          type: FormDocumentsInputType.DATE,
          placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
          show: () => !isGoodsItem && !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: () => isBEHighValue }),
        },
      },
    },
    [DeclarationDocumentsEnum.AUTHORISATION_DOCUMENTS]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.AUTHORISATION_DOCUMENTS],
      declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
      fields: {
        type: {
          type: FormDocumentsInputType.MULTILANG,
          code: ['CL605', 'CL615'],
          placeholder: PLACEHOLDER.TYPE,
          noWhitespace: true,
        },
        referenceNumber: {
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Reference',
          required: true,
        },
        holderOfTheAuthorisation: {
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Holder of the authorisation',
          noWhitespace: true,
        },
      },
    },
    [DeclarationDocumentsEnum.ADDITIONS_AND_DEDUCTIONS]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.ADDITIONS_AND_DEDUCTIONS],
      declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
      fields: {
        code: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL791',
          placeholder: PLACEHOLDER.CODE,
          noWhitespace: true,
        },
        amount: {
          type: FormDocumentsInputType.NUMBER,
          placeholder: PLACEHOLDER.AMOUNT,
        },
      },
    },
    [DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.PREVIOUS_DOCUMENTS],
      fields: {
        type: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL214',
          placeholder: PLACEHOLDER.TYPE,
          noWhitespace: true,
          'data-pw': 'type-input',
        },
        referenceNumber: {
          type: FormDocumentsInputType.INPUT,
          placeholder: PLACEHOLDER.REFERENCE_NUMBER,
          'data-pw': 'reference-number-input',
          required: true,
        },
        dateOfValidity: {
          type: FormDocumentsInputType.DATE,
          placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
          'data-pw': 'date-of-validity-input',
          show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: () => isBEHighValue }),
        },
        category: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Category',
          show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: () => isBEHighValue }),
        },
        location: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Location',
          show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: () => isBEHighValue }),
        },
        article: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Article',
          show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: () => isBEHighValue }),
        },
        billOfLoading: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Bill of loading',
          show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: () => isBEHighValue }),
        },
        item: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Item',
          show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: () => isBEHighValue }),
        },
        typeOfPackages: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL017',
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          placeholder: 'Type of packages',
          noWhitespace: true,
          show: (context) => !context?.isHeader && country !== EnvironmentCountries.NL,
        },
        numberOfPackages: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.NUMBER,
          placeholder: 'Number of packages',
          show: (context) => !context?.isHeader && country !== EnvironmentCountries.NL,
        },
        measurementUnitAndQualifier: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL349',
          placeholder: PLACEHOLDER.MEASUREMENT_UNIT,
          noWhitespace: true,
          show: (context) => !context?.isHeader && country !== EnvironmentCountries.NL,
        },
        quantity: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.NUMBER,
          placeholder: PLACEHOLDER.QUANTITY,
          show: (context) => !context?.isHeader && country !== EnvironmentCountries.NL,
        },
        goodsItemIdentifier: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Goods item identifier',
          noWhitespace: true,
          show: (context) => !context?.isHeader,
        },
      },
    },
    [DeclarationDocumentsEnum.SUPPORTING_DOCUMENTS]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.SUPPORTING_DOCUMENTS],
      fields: {
        type: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL213',
          placeholder: PLACEHOLDER.TYPE,
          noWhitespace: true,
        },
        referenceNumber: {
          type: FormDocumentsInputType.INPUT,
          placeholder: PLACEHOLDER.REFERENCE_NUMBER,
          required: true,
        },
        dateOfValidity: {
          type: FormDocumentsInputType.DATE,
          placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
          show: () => !hideElementFunction({ systems: ['DECO', 'IDMS_LV'], declarationGroup, country, condition: null }),
        },
        documentLineItemNumber: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.NUMBER,
          placeholder: 'Document line item number',
        },
        issuingAuthorityName: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Issuing authority name',
          show: () =>
            !hideElementFunction({
              declarationGroup,
              country,
              condition: () => declarationGroup === DeclarationHighValueGroupType.H2 && country === EnvironmentCountries.NL,
            }),
        },
        measurementUnitAndQualifier: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL349',
          placeholder: PLACEHOLDER.MEASUREMENT_UNIT,
          noWhitespace: true,
          show: (context) => !context?.isHeader,
        },
        quantity: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.NUMBER,
          placeholder: PLACEHOLDER.QUANTITY,
          show: (context) => !context?.isHeader,
        },
        currency: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.MULTILANG,
          code: isNLExport ? 'CL352' : 'CL048',
          placeholder: PLACEHOLDER.CURRENCY,
          noWhitespace: true,
          show: (context) => !context?.isHeader,
        },
        amount: {
          declarationType: FormDocumentsDeclarationType.HIGH_VALUES,
          type: FormDocumentsInputType.NUMBER,
          placeholder: PLACEHOLDER.AMOUNT,
          show: (context) => !context?.isHeader,
        },
      },
    },
    [DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.GUARANTEE_DOCUMENTS],
      fields: {
        type: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL251',
          placeholder: PLACEHOLDER.TYPE,
          noWhitespace: true,
        },
        'reference.dateOfValidity': {
          type: FormDocumentsInputType.DATE,
          placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
          show: () => !hideElementFunction({ systems: ['DMS'], declarationGroup, country, condition: null }),
        },
        'reference.grn': {
          type: FormDocumentsInputType.INPUT,
          placeholder: 'GRN',
          noWhitespace: true,
        },
        'reference.accessCode': {
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Access Code',
          noWhitespace: true,
        },
        'reference.currencyCode': {
          type: FormDocumentsInputType.MULTILANG,
          code: isNLExport ? 'CL352' : 'CL048',
          placeholder: PLACEHOLDER.CURRENCY,
          noWhitespace: true,
        },
        'reference.amountTobeCovered': {
          type: FormDocumentsInputType.NUMBER,
          placeholder: 'Amount to be covered',
        },
        'reference.otherGuaranteeReference': {
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Other guarantee reference',
        },
        'reference.customsOfficeGuarantee.referenceNumber': {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL141',
          placeholder: 'Customs Office Reference Number',
        },
      },
    },
    [DeclarationDocumentsEnum.ADDITIONAL_REFERENCE]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.ADDITIONAL_REFERENCE],
      fields: {
        type: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL380',
          placeholder: PLACEHOLDER.TYPE,
          noWhitespace: true,
        },
        referenceNumber: {
          type: FormDocumentsInputType.INPUT,
          placeholder: PLACEHOLDER.REFERENCE_NUMBER,
          'data-pw': 'reference-number-input',
          required: true,
        },
        dateOfValidity: {
          type: FormDocumentsInputType.DATE,
          placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
          show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: () => isBEHighValue }),
        },
        producedDocumentsValidationOffice: {
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Produced documents validation office',
          show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: () => isBEHighValue }),
        },
      },
    },
    [DeclarationDocumentsEnum.ADDITIONAL_INFORMATION]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.ADDITIONAL_INFORMATION],
      fields: {
        code: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL239',
          placeholder: PLACEHOLDER.CODE,
          noWhitespace: true,
        },
        text: {
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Text',
          trimWhitespace: true,
        },
        dateOfValidity: {
          type: FormDocumentsInputType.DATE,
          placeholder: PLACEHOLDER.DATE_OF_VALIDITY,
          show: () => !hideElementFunction({ systems: ['DMS', 'DECO', 'IDMS_LV'], declarationGroup, country, condition: () => isBEHighValue }),
        },
      },
    },
    [DeclarationDocumentsEnum.WAREHOUSE_DOCUMENT]: {
      id: declarationDocumentsIdsMapping[DeclarationDocumentsEnum.WAREHOUSE_DOCUMENT],
      fields: {
        type: {
          type: FormDocumentsInputType.MULTILANG,
          code: 'CL099',
          placeholder: PLACEHOLDER.TYPE,
          noWhitespace: true,
        },
        identifier: {
          type: FormDocumentsInputType.INPUT,
          placeholder: 'Identifier',
          noWhitespace: true,
          required: true,
        },
      },
    },
  };
};
